/* eslint-disable react-hooks/exhaustive-deps */

import { Badge, Box, Button, Flex, Stack, Text } from '@chakra-ui/react';
import { CheckCircle, Microphone } from '@phosphor-icons/react';
import { format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadingSkeleton } from '@/components/layout/LoadingSkeleton';
import { fetchAPI } from '@/api/fetch';
import { toaster } from '@/components/ui/toaster';
import {
  apiGetScreeningStatistics,
  apiGetScreenings,
  GetScreeningStatisticsResponse,
  GetScreeningsResponse,
  apiReviewScreening
} from '@/api/services';
import { apiGetAccountProfile } from '@/api/services/account.service';
import { Screening } from '@/types/Screening';
import {
  expandScreeningStatus,
  getScreeningTypeColor,
  getScreeningTypeLabel,
  ScreeningStatus,
  ScreeningType,
  AgencyPlan,
  AgencyUsageType
} from '@/types/enums';

import RecentSection from './RecentSection';
import { getCandidateFullName } from '@/utils';
import useAuth from '@/store/auth';
import { LimitExhaustedDialog } from './LimitExhaustedDialog';

export interface DateScreening {
  date: Date;
  screenings: Screening[];
}

interface AwaitingReview {
  screenings: Screening[];
  total_count: number;
  page: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
}

export default function AgentIndex({
  setUnreadCount
}: {
  setUnreadCount: (count: number) => void;
}) {
  const navigate = useNavigate();
  const { account } = useAuth();
  const [isRecentSessionLoading, setIsRecentSessionLoading] = useState(false);
  const [isRecentSessionLoadingMore, setIsRecentSessionLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ScreeningStatus | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [recentScreenings, setRecentScreenings] = useState<DateScreening[]>([]);
  const [awaitingReview, setAwaitingReview] = useState<AwaitingReview>({
    screenings: [],
    total_count: 0,
    page: 1,
    hasMore: true,
    isLoading: false,
    isLoadingMore: true,
    error: null
  });
  const awaitingReviewContainerRef = useRef<HTMLDivElement>(null);
  const [statistics, setStatistics] = useState<GetScreeningStatisticsResponse | null>(null);
  const [isLimitDialogOpen, setIsLimitDialogOpen] = useState(false);

  const PAGE_SIZE = 30;

  const [isFreePlan, setIsFreePlan] = useState(false);

  const handleAddSession = async () => {
    // Fetch fresh account data to check latest limits
    try {
      const response = await fetchAPI<Account>({
        request: () => apiGetAccountProfile()
      });

      if (response.success && response.data) {
        const freshAccount = response.data;
        const agenticInterviewsUsage = freshAccount.agency?.usages?.find(
          (usage) => usage.type === AgencyUsageType.AgenticInterviews
        );
        const isExhausted = agenticInterviewsUsage?.exhausted || false;
        const isPlanFree = freshAccount.agency?.plan === AgencyPlan.FREE;

        if (isExhausted || isPlanFree) {
          setIsFreePlan(isPlanFree);
          setIsLimitDialogOpen(true);
        } else {
          navigate('/interview/agent/create-session');
        }
      } else {
        // If API call fails, allow navigation
        navigate('/interview/agent/create-session');
      }
    } catch {
      // If error occurs, allow navigation
      navigate('/interview/agent/create-session');
    }
  };

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await fetchAPI<GetScreeningStatisticsResponse>({
        request: () => apiGetScreeningStatistics()
      });

      if (response.success) {
        setStatistics(response.data);
      } else {
        console.error('Failed to fetch statistics:', response.message);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }, []);

  const fetchScreenings = useCallback(
    async ({ page, status }: { page: number; status: ScreeningStatus | null }) => {
      if (page === 1) {
        setIsRecentSessionLoading(true);
      } else {
        setIsRecentSessionLoadingMore(true);
      }
      setError(null);

      try {
        let filterStatus = null;
        if (status) {
          filterStatus = expandScreeningStatus(status);
        }
        const response = await fetchAPI<GetScreeningsResponse>({
          request: () =>
            apiGetScreenings({
              params: {
                page: page,
                limit: PAGE_SIZE,
                status: filterStatus || undefined
              }
            })
        });

        if (response.success) {
          const { screenings, current_page, total_pages } = response.data;

          // Group screenings by date
          const groupedScreenings = screenings.reduce(
            (acc: Record<string, Screening[]>, screening) => {
              const date = new Date(screening.deadline * 1000).toDateString();
              if (!acc[date]) {
                acc[date] = [];
              }
              acc[date].push(screening);
              return acc;
            },
            {}
          );

          // Convert to DateScreening format and sort by date (newest first)
          const dateScreenings: DateScreening[] = Object.entries(groupedScreenings)
            .map(([dateStr, screenings]) => ({
              date: new Date(dateStr),
              screenings: screenings.sort((a, b) => b.deadline - a.deadline)
            }))
            .sort((a, b) => b.date.getTime() - a.date.getTime());

          if (page === 1) {
            setRecentScreenings(dateScreenings);
          } else {
            setRecentScreenings((prev) => [...prev, ...dateScreenings]);
          }

          setHasMore(current_page < total_pages);
          setPage(current_page);
        } else {
          setError('Failed to fetch screenings. Please try again later.');
          console.error('Failed to fetch screenings:', response.message);
        }
      } catch (error) {
        console.error('Error fetching screenings:', error);
        setError('An error occurred while fetching screenings. Please try again later.');
      }

      setIsRecentSessionLoading(false);
      setIsRecentSessionLoadingMore(false);
    },
    []
  );

  const fetchAwaitingReview = useCallback(
    async (pageToFetch: number = 1, append: boolean = false) => {
      if (pageToFetch === 1 && !append) {
        setAwaitingReview((prev) => ({ ...prev, isLoading: true, error: null }));
      } else {
        setAwaitingReview((prev) => ({ ...prev, isLoadingMore: true, error: null }));
      }

      try {
        const response = await fetchAPI<GetScreeningsResponse>({
          request: () =>
            apiGetScreenings({
              params: {
                page: pageToFetch,
                limit: PAGE_SIZE,
                awaiting_review: true
              }
            })
        });

        if (response.success) {
          const { screenings, current_page, total_pages, total_count } = response.data;
          setAwaitingReview((prev) => ({
            ...prev,
            screenings: append ? [...prev.screenings, ...screenings] : screenings,
            page: current_page,
            total_count: total_count,
            hasMore: current_page < total_pages,
            isLoading: false,
            isLoadingMore: false
          }));

          setUnreadCount(total_count);
        } else {
          setAwaitingReview((prev) => ({
            ...prev,
            error: 'Failed to fetch awaiting review screenings.',
            isLoading: false,
            isLoadingMore: false
          }));
          console.error('Failed to fetch awaiting review screenings:', response.message);
        }
      } catch (error) {
        console.error('Error fetching awaiting review screenings:', error);
        setAwaitingReview((prev) => ({
          ...prev,
          error: 'An error occurred while fetching awaiting review screenings.',
          isLoading: false,
          isLoadingMore: false
        }));
      }
    },
    []
  );

  const handleAwaitingReviewLoadMore = useCallback(() => {
    if (awaitingReview.isLoadingMore || !awaitingReview.hasMore) return;
    fetchAwaitingReview(awaitingReview.page + 1, true);
  }, [
    awaitingReview.isLoadingMore,
    awaitingReview.hasMore,
    awaitingReview.page,
    fetchAwaitingReview
  ]);

  const handleLoadMore = useCallback(() => {
    if (isRecentSessionLoadingMore || !hasMore) return;
    setPage((prevPage) => prevPage + 1);
  }, [isRecentSessionLoadingMore, hasMore]);

  const handleReviewScreening = useCallback(
    async (screeningId: string) => {
      try {
        const response = await fetchAPI<void>({
          request: () => apiReviewScreening(screeningId)
        });

        if (response.success) {
          // Reload awaiting review data after successful review
          await fetchAwaitingReview();
        } else {
          toaster.error({
            title: 'Error',
            description: 'Failed to review screening. Please try again.'
          });
          console.error('Failed to review screening:', response.message);
        }
      } catch (error) {
        toaster.error({
          title: 'Error',
          description: 'An error occurred while reviewing the screening. Please try again.'
        });
        console.error('Error reviewing screening:', error);
      }
    },
    [fetchAwaitingReview, fetchStatistics]
  );

  useEffect(() => {
    fetchStatistics();
    fetchScreenings({ page: 1, status: statusFilter });
    fetchAwaitingReview();
  }, [fetchStatistics, fetchScreenings, fetchAwaitingReview, statusFilter]);

  useEffect(() => {
    if (page > 1) {
      fetchScreenings({ page, status: statusFilter });
    }
  }, [page, fetchScreenings, statusFilter]);

  useEffect(() => {
    const container = awaitingReviewContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (
        scrollHeight - scrollTop <= clientHeight * 1.2 &&
        !awaitingReview.isLoadingMore &&
        awaitingReview.hasMore
      ) {
        handleAwaitingReviewLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [awaitingReview.isLoadingMore, awaitingReview.hasMore, handleAwaitingReviewLoadMore]);

  const handleUpdateScreening = (updatedScreening: Screening, removed: boolean) => {
    if (removed) {
      // Remove from recentScreenings
      setRecentScreenings((prev) =>
        prev
          .map((dateGroup) => ({
            ...dateGroup,
            screenings: dateGroup.screenings.filter(
              (screening) => screening.screening_id !== updatedScreening.screening_id
            )
          }))
          .filter((dateGroup) => dateGroup.screenings.length > 0)
      );
      // Remove from awaitingReview
      setAwaitingReview((prev) => ({
        ...prev,
        screenings: prev.screenings.filter(
          (screening) => screening.screening_id !== updatedScreening.screening_id
        )
      }));
    } else {
      // Move updatedScreening to the correct dateGroup in recentScreenings
      setRecentScreenings((prev) => {
        // Remove the screening from all dateGroups
        const withoutScreening = prev.map((dateGroup) => {
          const filtered = dateGroup.screenings.filter((screening) => {
            if (screening.screening_id === updatedScreening.screening_id) {
              return false;
            }
            return true;
          });
          return { ...dateGroup, screenings: filtered };
        });

        // Remove empty dateGroups
        const cleaned = withoutScreening.filter((dateGroup) => dateGroup.screenings.length > 0);

        // Find the dateGroup for the updatedScreening's deadline
        const updatedDate = new Date(updatedScreening.deadline * 1000);
        const updatedDateString = updatedDate.toDateString();

        let found = false;
        const newRecentScreenings = cleaned.map((dateGroup) => {
          const groupDate = new Date(dateGroup.date).toDateString();
          if (groupDate === updatedDateString) {
            found = true;
            // Insert updatedScreening, sorted by deadline descending
            const newScreenings = [...dateGroup.screenings, updatedScreening].sort(
              (a, b) => b.deadline - a.deadline
            );
            return { ...dateGroup, screenings: newScreenings };
          }
          return dateGroup;
        });

        // If no dateGroup found, create a new one
        if (!found) {
          newRecentScreenings.push({
            date: updatedDate,
            screenings: [updatedScreening]
          });
        }

        // Sort dateGroups by date descending
        return newRecentScreenings.sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
      });

      // Update in awaitingReview if present
      setAwaitingReview((prev) => ({
        ...prev,
        screenings: prev.screenings.map((screening) =>
          screening.screening_id === updatedScreening.screening_id ? updatedScreening : screening
        )
      }));
    }
  };

  if (error) {
    return (
      <Flex w="100%" h="100%" align="center" mt="30%" fontSize="16px" justify="center" role="alert">
        <Text color="error.500">{error}</Text>
      </Flex>
    );
  }

  return (
    <Flex w="100%" justify="flex-start" align="flex-start" gap="24px" h="100%">
      <Box flex={5}>
        <RecentSection
          recentScreenings={recentScreenings}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          isLoading={isRecentSessionLoading}
          isLoadingMore={isRecentSessionLoadingMore}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
          onUpdateScreening={handleUpdateScreening}
        />
      </Box>
      <Stack flex={3} h="calc(100vh - 200px)" gap="12px">
        <Box
          w="100%"
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="20px 32px"
          boxShadow="sm"
          _hover={{ boxShadow: 'md' }}
          transition="all 0.2s ease-in-out"
        >
          <Text fontSize="20px" fontWeight="bold" mb="8px">
            Overview
          </Text>
          <Box
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="8px"
            p="8px 16px"
            bg="background.primary"
          >
            <Stack gap="12px">
              <Flex justify="space-between" align="center" w="100%">
                <Text fontSize="14px" color="neutrals.700">
                  Number of Sessions
                </Text>
                <Text fontSize="18px" color="neutrals.900">
                  {statistics?.total_sessions || 0}
                </Text>
              </Flex>
              <Flex flex="1" gap="64px">
                <Stack flex="1" gap="12px">
                  <Flex justify="space-between" align="center" w="100%">
                    <Text fontSize="14px" color="neutrals.700">
                      Scheduled
                    </Text>
                    <Text fontSize="14px" color="neutrals.900">
                      {statistics?.scheduled_sessions || 0}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="center" w="100%">
                    <Text fontSize="14px" color="neutrals.700">
                      Completed
                    </Text>
                    <Text fontSize="14px" color="neutrals.900">
                      {statistics?.completed_sessions || 0}
                    </Text>
                  </Flex>
                </Stack>
                <Stack flex="1" gap="12px">
                  <Flex justify="space-between" align="center" w="100%">
                    <Text fontSize="14px" color="neutrals.700">
                      In Progress
                    </Text>
                    <Text fontSize="14px" color="neutrals.900">
                      {statistics?.in_progress_sessions || 0}
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="center" w="100%">
                    <Text fontSize="14px" color="neutrals.700">
                      Expired
                    </Text>
                    <Text fontSize="14px" color="neutrals.900">
                      {statistics?.expired_sessions || 0}
                    </Text>
                  </Flex>
                </Stack>
              </Flex>
            </Stack>
          </Box>
        </Box>
        <Box
          w="100%"
          h="100%"
          maxH="calc(100vh - 400px)"
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          p="32px 32px"
          boxShadow="sm"
          _hover={{ boxShadow: 'md' }}
          transition="all 0.2s ease-in-out"
        >
          <Stack mb="24px" gap="4px">
            <Flex justify="space-between" align="center">
              <Text fontSize="20px" fontWeight="bold">
                Awaiting Review
              </Text>
              <Badge
                fontSize="16px"
                w={awaitingReview.total_count > 99 ? '36px' : '26px'}
                h={awaitingReview.total_count > 99 ? '28px' : '26px'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
                bg="primary.900"
                color="neutrals.0"
              >
                {awaitingReview.total_count > 99 ? '99+' : awaitingReview.total_count}
              </Badge>
            </Flex>
          </Stack>
          {awaitingReview.isLoading ? (
            <Stack
              ref={awaitingReviewContainerRef}
              gap="16px"
              maxH="calc(100vh - 530px)"
              overflowY="auto"
            >
              <LoadingSkeleton count={2} h="100px" />
            </Stack>
          ) : awaitingReview.screenings.length > 0 ? (
            <Stack
              ref={awaitingReviewContainerRef}
              gap="16px"
              maxH="calc(100vh - 530px)"
              overflowY="auto"
            >
              {awaitingReview.screenings.map((screening: Screening) => (
                <Box
                  key={screening.screening_id}
                  w="100%"
                  border="1px solid"
                  borderColor="neutrals.200"
                  borderRadius="8px"
                  p="16px"
                  bg="background.primary"
                  cursor="pointer"
                  _hover={{
                    boxShadow: 'sm'
                  }}
                  transition="all 0.2s ease-in-out"
                  onClick={() => {
                    if (screening.screening_type === ScreeningType.JobInterview) {
                      navigate(
                        `/job/details/${screening.job_id}/candidate/${screening.candidate_id}`
                      );
                    } else {
                      navigate(`/candidate/${screening.candidate_id}`);
                    }
                  }}
                >
                  <Stack
                    gap={screening.screening_type === ScreeningType.JobInterview ? '2px' : '12px'}
                  >
                    <Flex justify="space-between" align="flex-start" gap="4px" h="100%">
                      <Stack>
                        {screening.screening_type === ScreeningType.JobInterview && (
                          <Text fontSize="14px" fontWeight="semibold" color="neutrals.500">
                            {screening.job?.client.name || 'Unknown Client'} -{' '}
                            {screening.job?.job_title || 'Unknown Position'}
                          </Text>
                        )}
                        <Text fontSize="16px" color="neutrals.900">
                          {screening.candidate
                            ? getCandidateFullName(
                                screening.candidate.first_name,
                                screening.candidate.last_name
                              )
                            : 'Unknown Candidate'}
                        </Text>
                      </Stack>
                      <Button
                        variant="ghost"
                        color="success.700"
                        p="0"
                        h="fit-content"
                        w="fit-content"
                        _hover={{ bg: 'success.100' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReviewScreening(screening.screening_id);
                        }}
                      >
                        <CheckCircle size={20} aria-hidden="true" />
                      </Button>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="14px" color="neutrals.700">
                        Completed{' '}
                        {screening.completed_at
                          ? format(new Date(screening.completed_at * 1000), 'MMM d')
                          : 'N/A'}
                      </Text>
                      <Badge
                        mt="auto"
                        borderRadius="12px"
                        bg={getScreeningTypeColor(screening.screening_type)}
                        p="2px 6px"
                      >
                        <Text fontSize="12px" color="neutrals.700">
                          {getScreeningTypeLabel(screening.screening_type)}
                        </Text>
                      </Badge>
                    </Flex>
                  </Stack>
                </Box>
              ))}
              {awaitingReview.isLoadingMore && <LoadingSkeleton count={2} h="100px" gap="16px" />}
            </Stack>
          ) : (
            <Button
              w="100%"
              color="neutrals.0"
              bg="primary.900"
              borderRadius="8px"
              fontWeight="semibold"
              fontSize="14px"
              p="10px 24px"
              _hover={{ bg: 'primary.700' }}
              aria-label="Add a new session"
              onClick={handleAddSession}
            >
              Add a Session
              <Microphone size={24} aria-hidden="true" />
            </Button>
          )}
        </Box>
      </Stack>
      <LimitExhaustedDialog
        isOpen={isLimitDialogOpen}
        onClose={() => setIsLimitDialogOpen(false)}
        isFreePlan={isFreePlan}
        usageType="agentic-interview"
      />
    </Flex>
  );
}
